import { useRef, useState, useCallback, useEffect } from 'react'

export interface Offset {
  x: number
  y: number
}

export interface DraggableHook {
  ref: React.RefObject<HTMLDivElement>
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void
  dragging: boolean
}

const useDraggable = (): DraggableHook => {
  const ref = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 })

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName.toLowerCase() === 'input') {
      return;
    }
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
      })
      setDragging(true)
    }
  }, [])

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (dragging && ref.current) {
      ref.current.style.left = `${e.clientX - offset.x}px`
      ref.current.style.top = `${e.clientY - offset.y}px`
    }
  },[dragging, offset])

  const onMouseUp = useCallback(() => {setDragging(false)}, [])

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    } else {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [dragging, onMouseMove, onMouseUp])

  return { ref, onMouseDown, dragging }
}

export default useDraggable;